import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  BadgeCheck,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  UserCircle2,
  X,
} from "lucide-react";
import { officerProfileApi } from "../../lib/api/handlers/officerProfile";
 import type {
  OfficerProfile,
  OfficerProfileUpdateRequest,
} from "../../lib/api/types";
 
import { useAuthSession } from "../../lib/auth/session";

type ProfileSettingsPanelProps = {
  open: boolean;
  onClose: () => void;
  officerName?: string;
  officerRole?: string;
  officerEmail?: string;
  officerPhone?: string;
  dutyStation?: string;
};

type ProfileFormState = {
  fullName: string;
  jobTitle: string;
  email: string;
  phone: string;
  alternatePhone: string;
  province: string;
  dutyStation: string;
  supervisor: string;
  languages: string;
  expertise: string;
  biography: string;
  shiftPattern: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  availableForEmergency: boolean;
  availableForNightShift: boolean;
  prefersSmsEscalations: boolean;
};

const defaultProfileState: ProfileFormState = {
  fullName: "",
  jobTitle: "",
  email: "",
  phone: "",
  alternatePhone: "",
  province: "",
  dutyStation: "",
  supervisor: "",
  languages: "",
  expertise: "",
  biography: "",
  shiftPattern: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  availableForEmergency: false,
  availableForNightShift: false,
  prefersSmsEscalations: false,
};

function createProfileState(
  overrides: Partial<ProfileFormState> = {},
): ProfileFormState {
  return {
    ...defaultProfileState,
    ...overrides,
  };
}

function mapOfficerProfileToForm(
  profile: OfficerProfile,
  fallback: ProfileFormState,
): ProfileFormState {
  return createProfileState({
    ...fallback,
    fullName: profile.fullName ?? fallback.fullName,
    jobTitle: profile.jobTitle ?? fallback.jobTitle,
    email: profile.email ?? fallback.email,
    phone: profile.phone ?? fallback.phone,
    alternatePhone: profile.alternatePhone ?? fallback.alternatePhone,
    province: profile.province ?? fallback.province,
    dutyStation: profile.dutyStation ?? fallback.dutyStation,
    supervisor: profile.supervisor ?? fallback.supervisor,
    languages: profile.languages ?? fallback.languages,
    expertise: profile.expertise ?? fallback.expertise,
    biography: profile.biography ?? fallback.biography,
    shiftPattern: profile.shiftPattern ?? fallback.shiftPattern,
     emergencyContactName:
 
      profile.emergencyContactName ?? fallback.emergencyContactName,
    emergencyContactPhone:
      profile.emergencyContactPhone ?? fallback.emergencyContactPhone,
    availableForEmergency:
      profile.availableForEmergency ?? fallback.availableForEmergency,
    availableForNightShift:
      profile.availableForNightShift ?? fallback.availableForNightShift,
    prefersSmsEscalations:
      profile.prefersSmsEscalations ?? fallback.prefersSmsEscalations,
 
  });
}

function normalizeText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

 function createUpdatePayload(
  state: ProfileFormState,
): OfficerProfileUpdateRequest {
 
  const fullName = state.fullName.trim();
  return {
    fullName: fullName.length > 0 ? fullName : state.fullName,
    jobTitle: normalizeText(state.jobTitle),
    email: normalizeText(state.email),
    phone: normalizeText(state.phone),
    alternatePhone: normalizeText(state.alternatePhone),
    province: normalizeText(state.province),
    dutyStation: normalizeText(state.dutyStation),
    supervisor: normalizeText(state.supervisor),
    languages: normalizeText(state.languages),
    expertise: normalizeText(state.expertise),
    biography: normalizeText(state.biography),
    shiftPattern: normalizeText(state.shiftPattern),
    emergencyContactName: normalizeText(state.emergencyContactName),
    emergencyContactPhone: normalizeText(state.emergencyContactPhone),
    availableForEmergency: state.availableForEmergency,
    availableForNightShift: state.availableForNightShift,
    prefersSmsEscalations: state.prefersSmsEscalations,
  };
}

function parseReviewDate(value?: string | null): Date | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveErrorMessage(error: unknown): string {
  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Something went wrong while syncing the profile. Please try again.";
}

export function ProfileSettingsPanel({
  open,
  onClose,
  officerName,
  officerRole,
  officerEmail,
  officerPhone,
  dutyStation,
}: ProfileSettingsPanelProps) {
  const { officerId } = useAuthSession();
  const fallbackProfile = useMemo(
    () =>
      createProfileState({
        fullName: officerName ?? "",
        jobTitle: officerRole ?? "",
        email: officerEmail ?? "",
        phone: officerPhone ?? "",
        dutyStation: dutyStation ?? "",
      }),
    [dutyStation, officerEmail, officerName, officerPhone, officerRole],
  );

  const [formState, setFormState] = useState<ProfileFormState>(fallbackProfile);
   const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">(
    "idle",
  );
 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastReviewed, setLastReviewed] = useState<Date | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setLoading(true);
    setStatus("idle");
    setError(null);

    if (!officerId) {
      setLoading(false);
      setError("Unable to load profile without an active officer session.");
      setStatus("error");
      setFormState(fallbackProfile);
 
      setLastReviewed(null);
      return;
    }

    let cancelled = false;

    officerProfileApi
      .get(officerId)
      .then((profile) => {
        if (cancelled) {
          return;
        }
        const normalized = mapOfficerProfileToForm(profile, fallbackProfile);
         const reviewedAt =
          parseReviewDate(profile.lastReviewedAt) ?? new Date();
        setFormState(normalized);
 
        setLastReviewed(reviewedAt);
        setStatus("idle");
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return;
        }
        setError(resolveErrorMessage(cause));
        setStatus("error");
        setFormState(fallbackProfile);
 
        setLastReviewed(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fallbackProfile, officerId, open]);

  const completion = useMemo(() => {
    const trackedValues: (string | boolean)[] = [
      formState.jobTitle,
      formState.email,
      formState.phone,
      formState.alternatePhone,
      formState.province,
      formState.dutyStation,
      formState.languages,
      formState.expertise,
      formState.supervisor,
      formState.emergencyContactName,
      formState.emergencyContactPhone,
      formState.availableForEmergency,
      formState.availableForNightShift,
      formState.prefersSmsEscalations,
    ];

    const filled = trackedValues.filter((value) => {
      if (typeof value === "string") {
        return value.trim().length > 0;
      }
      return value;
    }).length;

    return Math.round((filled / trackedValues.length) * 100);
  }, [formState]);

  const readinessAdvisories = useMemo(() => {
    const advisories: string[] = [];

    if (!formState.availableForEmergency) {
      advisories.push(
        "Enable emergency deployment availability for outbreak readiness rosters.",
      );
    }

    if (!formState.availableForNightShift) {
      advisories.push(
        "Night shift coverage is limited. Flag availability if rotation permits.",
      );
    }

    if (!formState.prefersSmsEscalations) {
      advisories.push(
        "SMS escalations recommended for rapid quarantine referrals.",
      );
    }

    return advisories;
  }, [formState]);

  function updateField(field: keyof ProfileFormState) {
    return (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const value = event.target.value;
      setFormState((prev) => ({ ...prev, [field]: value }));
    };
  }

  function updateBoolean(field: keyof ProfileFormState) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setFormState((prev) => ({ ...prev, [field]: checked }));
    };
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loading) {
      return;
    }

    if (!officerId) {
      setError("Unable to save profile without an active officer session.");
      setStatus("error");
      return;
    }

    const previousState = formState;

    setStatus("saving");
    setError(null);

    try {
      const payload = createUpdatePayload(formState);
      const response = await officerProfileApi.update(officerId, payload);
      const normalized = mapOfficerProfileToForm(response, previousState);
      const reviewedAt = parseReviewDate(response.lastReviewedAt) ?? new Date();
      setFormState(normalized);
 
      setLastReviewed(reviewedAt);
      setStatus("saved");
    } catch (cause: unknown) {
      setError(resolveErrorMessage(cause));
      setStatus("error");
    }
  }

   const lastReviewedDisplay = lastReviewed
    ? lastReviewed.toLocaleString()
    : "—";
  const formDisabled = loading || status === "saving";
 

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl rounded-3xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div className="space-y-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              <UserCircle2 className="h-4 w-4" /> Profile Settings
            </span>
            <h2 className="text-xl font-semibold text-slate-900">
              {formState.fullName}
            </h2>
            <p className="max-w-2xl text-sm text-slate-600">
              Update officer details, contact channels, and deployment
              preferences. Changes sync to duty rosters and maker/checker
              dashboards.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-600">
               <Clock className="h-3.5 w-3.5" /> Last reviewed{" "}
              {lastReviewedDisplay}
 
            </span>
            {loading && (
              <span className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 font-medium text-sky-700">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Syncing
                profile…
 
              </span>
            )}
            {status === "saving" && (
              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 font-medium text-indigo-700">
                <ShieldCheck className="h-3.5 w-3.5" /> Saving changes…
              </span>
            )}
            {status === "saved" && (
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 font-medium text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Profile saved
              </span>
            )}
            {status === "error" && (
              <span className="inline-flex items-center gap-2 rounded-full bg-rose-100 px-3 py-1 font-medium text-rose-700">
                <AlertCircle className="h-3.5 w-3.5" /> Sync issue
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100"
            aria-label="Close profile settings"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

         <form
 
          id="profile-settings-form"
          onSubmit={handleSubmit}
          className="flex flex-col"
        >
          <div className="space-y-6 px-6 py-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            <fieldset
               disabled={formDisabled}
 
              className="grid gap-6 lg:grid-cols-[2fr,1fr]"
            >
              <div className="space-y-6">
                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-slate-900">
                    Identification & role
                  </h3>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="text-sm">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Full name
                      </span>
                      <input
                        value={formState.fullName}
                        onChange={updateField("fullName")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="Officer name"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Job title
                      </span>
                      <input
                        value={formState.jobTitle}
                        onChange={updateField("jobTitle")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Supervisor
                      </span>
                      <input
                        value={formState.supervisor}
                        onChange={updateField("supervisor")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Languages
                      </span>
                      <input
                        value={formState.languages}
                        onChange={updateField("languages")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="English, Shona"
                      />
                    </label>
                  </div>
                  <label className="mt-4 block text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Professional summary
                    </span>
                    <textarea
                      value={formState.biography}
                      onChange={updateField("biography")}
                      rows={3}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Highlight responsibilities and domain expertise"
                    />
                  </label>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-slate-900">
                    Contact channels
                  </h3>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="text-sm">
                      <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                        <Mail className="h-3.5 w-3.5 text-blue-500" /> Email
                      </span>
                      <input
                        type="email"
                        value={formState.email}
                        onChange={updateField("email")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="officer@porthealth.gov.zw"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                        <Phone className="h-3.5 w-3.5 text-emerald-500" />{" "}
                        Primary mobile
                      </span>
                      <input
                        value={formState.phone}
                        onChange={updateField("phone")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="+263 7XX XXX XXX"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Alternate contact
                      </span>
                      <input
                        value={formState.alternatePhone}
                        onChange={updateField("alternatePhone")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="Desk line or landline"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Emergency contact
                      </span>
                      <input
                        value={formState.emergencyContactName}
                        onChange={updateField("emergencyContactName")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="Name"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Emergency contact number
                      </span>
                      <input
                        value={formState.emergencyContactPhone}
                        onChange={updateField("emergencyContactPhone")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="+263 …"
                      />
                    </label>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-slate-900">
                    Coverage & deployments
                  </h3>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="text-sm">
                      <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                        <MapPin className="h-3.5 w-3.5 text-purple-500" /> Duty
                        station
                      </span>
                      <input
                        value={formState.dutyStation}
                        onChange={updateField("dutyStation")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Province / coverage
                      </span>
                      <input
                        value={formState.province}
                        onChange={updateField("province")}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="Harare Province"
                      />
                    </label>
                  </div>
                  <label className="mt-4 block text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Specialised expertise
                    </span>
                    <textarea
                      value={formState.expertise}
                      onChange={updateField("expertise")}
                      rows={3}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="e.g. yellow fever verification, cargo hazardous inspections"
                    />
                  </label>
                  <label className="mt-4 block text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Shift pattern
                    </span>
                    <textarea
                      value={formState.shiftPattern}
                      onChange={updateField("shiftPattern")}
                      rows={2}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Document weekly rotation"
                    />
                  </label>
                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={formState.availableForEmergency}
                        onChange={updateBoolean("availableForEmergency")}
                        className="mt-1"
                      />
                      <span>
                        Emergency deployments
                        <span className="block text-xs text-slate-500">
                          Available for outbreak or quarantine surge duties.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={formState.availableForNightShift}
                        onChange={updateBoolean("availableForNightShift")}
                        className="mt-1"
                      />
                      <span>
                        Night shift coverage
                        <span className="block text-xs text-slate-500">
                          Opt in for overnight rotation scheduling.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={formState.prefersSmsEscalations}
                        onChange={updateBoolean("prefersSmsEscalations")}
                        className="mt-1"
                      />
                      <span>
                        SMS escalations
                        <span className="block text-xs text-slate-500">
                          Receive high priority incidents via SMS.
                        </span>
                      </span>
                    </label>
                  </div>
                </section>
              </div>

              <aside className="space-y-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between text-sm font-semibold text-slate-900">
                    <span>Profile readiness</span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                      {completion}%
                    </span>
                  </div>
                  <div className="mt-4 h-2 w-full rounded-full bg-slate-100">
                    <div
                      className="h-2 rounded-full bg-blue-500 transition-all"
                      style={{ width: `${completion}%` }}
                    />
                  </div>
                  <ul className="mt-4 space-y-2 text-xs text-slate-600">
                    <li className="flex items-center gap-2 text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Maker & checker
                      credentials verified
                    </li>
                    <li className="flex items-center gap-2 text-blue-600">
                      <BadgeCheck className="h-3.5 w-3.5" /> Smart card
                      provisioned for secure access
                    </li>
                    <li className="flex items-center gap-2 text-purple-600">
                      <Activity className="h-3.5 w-3.5" /> Duty roster syncs to
                      mobile every 15 minutes
                    </li>
                  </ul>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h4 className="text-sm font-semibold text-slate-900">
                    Coverage summary
                  </h4>
                  <p className="mt-2 text-sm text-slate-600">
                    Current deployment footprint.
                  </p>
                  <div className="mt-4 space-y-3 text-sm text-slate-600">
                    <p className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-500" />{" "}
                      {formState.dutyStation}
                    </p>
                    <p className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-emerald-500" />{" "}
                      {formState.shiftPattern}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h4 className="text-sm font-semibold text-slate-900">
                    Advisories
                  </h4>
                  {readinessAdvisories.length === 0 ? (
                    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                      <CheckCircle2 className="mb-2 h-4 w-4" /> Deployment
                      preferences meet readiness baseline.
                    </div>
                  ) : (
                    <ul className="mt-3 space-y-2 text-sm text-amber-700">
                      {readinessAdvisories.map((message) => (
                        <li
                          key={message}
                          className="rounded-xl border border-amber-200 bg-amber-50 p-3"
                        >
                          {message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </aside>
            </fieldset>
 
          </div>

          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
            <p className="text-xs text-slate-500">
              Adjusted settings sync across the PHMIS mobile app and duty roster
              exports.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-transparent px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-500 disabled:opacity-60"
                 disabled={formDisabled}
 
              >
                Save profile
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}
